import {
    AccountsAccountRow,
    AccountsColumn,
    AccountsRow,
    FilterGroup,
} from '../components/accounts-overview/accounts-overview.types';
import { INCOME_STATEMENT_POSITIONS } from '../constants/account-positions.constant';
import { IProjectAccount } from '../models/project-account.model';
import {
    AccountsPresentation,
    PresenterInput,
    matchesAccountSearch,
    statementOf,
    zeroAccountsChipOf,
} from './accounts-presentation';
import { PeriodPick, displayValuesOf, evaluateStatement } from './evaluate-statement';
import { flatStatementLayout, statementRowsOf, withUnknownPositions } from './statement-rows';

const NOTHING = 0;
const FIRST = 0;
const CURRENT = 0;
const PERCENT = 100;
/** The id of the section row over the statement; no layout node carries it. */
const INCOME_STATEMENT_SECTION_ID = 'incomeStatement';

/**
 * The base of the percentages while a backend without layouts answers: it still sends the old class
 * key for operating revenue.
 */
const LEGACY_OPERATING_REVENUE = 'INCOME_STATEMENT_OPERATING_REVENUE';

/**
 * The INCOME-STATEMENT screen: analyse the figures.
 *
 * A different screen, not the balance sheet with a flag:
 *
 *   - NO status and no status chips. Whether a document matches a balance is not a question the
 *     income statement asks, so showing an empty column would invite the answer "nothing reconciled".
 *   - Percentages of operating revenue beside every amount — the figure an income statement is read
 *     by, and one the balance sheet has no equivalent for.
 *   - An ABSOLUTE change column beside the relative one: on a result, "180 000 more" and "+8.4%" say
 *     different things and both are wanted.
 *   - Running subtotals between the groups (Bruttogewinn, EBITDA, EBIT …): the Staffelform.
 *
 * Every figure is in the display sign (`displaySignOf`): each line shows its effect on the result —
 * revenue positive, expense negative — so the lines, totals and subtotals add up to the result. The
 * structure (groups, totals, the running subtotals of the Staffelform) is the layout's, not this file's.
 */

/**
 * The two analysis toggles on the left. They change what the table SHOWS, not what it contains — no
 * request, no refetch.
 */
export const INCOME_STATEMENT_TOGGLES = ['percentOfRevenue', 'priorYearComparison'] as const;

export type IncomeStatementToggleId = (typeof INCOME_STATEMENT_TOGGLES)[number];

/**
 * Which toggles are on. Both on is the state the statement is normally read in.
 *
 * `priorYearComparison` switches the two CHANGE columns — Δ absolut and Δ%. It does NOT switch the
 * prior year itself: that column is always there, because a statement is read against last year.
 * What is optional is the comparison, not the comparison value.
 */
export interface IncomeStatementView {
    /** The percentage-of-revenue columns beside each amount. */
    readonly percentOfRevenue: boolean;
    /** The two change columns. The prior-year column stays either way. */
    readonly priorYearComparison: boolean;
}

/**
 * A comparison period on the right — `Vorjahre: 2024 2023 2022`.
 *
 * Selecting one costs a request: another period means another journal to download and decrypt. The
 * `id` is the period's ISO bounds joined, never a year — two periods can end in the same calendar
 * year after a change of balance-sheet date, and a year would then name neither.
 */
export interface ComparisonYear {
    readonly id: string;
    readonly label: string;
    readonly active: boolean;
}

/**
 * One comparison COLUMN, as the page resolved it.
 *
 * `index` points into `IProjectAccount.comparisons`, which is positional against what the response
 * reported. Carried explicitly rather than implied by array order, because a period the backend holds
 * no journal for gets no column while still occupying its position on every account — reading the two
 * as the same list is how a column ends up showing another period's figures.
 */
export interface ComparisonColumn {
    readonly index: number;
    readonly label: string;
}

/** Texts the page passes in, so no year and no translation is invented in here. */
export interface IncomeStatementLabels {
    readonly currentColumn: string;
    /**
     * The comparison columns, in display order. Empty means there is nothing to compare against, and
     * then the statement shows only the current period — no placeholder column.
     */
    readonly comparisonColumns: readonly ComparisonColumn[];
    readonly percentColumn: string;
    readonly deltaAbsoluteColumn: string;
    readonly deltaPercentColumn: string;
    readonly toggle: (id: IncomeStatementToggleId) => string;
    /** The heading in front of the comparison years. */
    readonly comparisonYearsLabel: string;
    /** The chip that shows accounts with no figures. Its own axis, not a third analysis toggle. */
    readonly zeroAccountsChip: string;
    /** Why the zero-accounts chip cannot be switched on yet; `null` or absent when it can. */
    readonly zeroAccountsLockedReason?: string | null;
    /** The section row on top, «Erfolgsrechnung in CHF». Absent: no section row. */
    readonly section?: string;
}

/** The accounts of THIS statement. Everything below counts over these, never over all. */
function accountsOfStatement(accounts: readonly IProjectAccount[]): IProjectAccount[] {
    return accounts.filter((account) => statementOf(account) === 'INCOME');
}

export function presentIncomeStatement(
    input: PresenterInput,
    labels: IncomeStatementLabels,
    view: IncomeStatementView,
    comparisonYears: readonly ComparisonYear[] = [],
    withZeroAccounts = false,
): AccountsPresentation {
    const mine = accountsOfStatement(input.accounts);

    // Period 0 is the current one; period n is comparison column n − 1.
    const periods: PeriodPick[] = [
        (account) => account.saldoInBaseCurrency.amount,
        ...labels.comparisonColumns.map(
            (comparison) => (account: IProjectAccount) => account.comparisons[comparison.index]?.saldo.amount ?? null,
        ),
    ];

    // Over ALL accounts of the statement: the search hides rows, never figures (REV-22006).
    const layout = withUnknownPositions(
        input.layout ?? flatStatementLayout('INCOME_STATEMENT', INCOME_STATEMENT_POSITIONS, LEGACY_OPERATING_REVENUE),
        mine,
    );
    const values = evaluateStatement(layout, mine, periods);
    const bases = basesOf(layout.percentBaseNodeId === undefined ? undefined : values.get(layout.percentBaseNodeId));
    const cells = cellPlanOf(labels, view, bases);
    const cellsOf = (rowValues: readonly (number | null)[]) => cells.map((cell) => ({ value: cell.of(rowValues) }));

    const statementRows = statementRowsOf({
        layout,
        accounts: mine,
        values,
        periods,
        isVisible: (account) => matchesAccountSearch(account, input.searchTerm),
        collapsed: input.collapsed,
        cellsOf,
        accountRowOf: (account): AccountsAccountRow => ({
            kind: 'account',
            id: account.accountId,
            number: account.accountNumber,
            label: account.name,
            values: cellsOf(displayValuesOf(account, periods)),
        }),
        labelOf: input.labelOf,
        layoutLabelOf: input.layoutLabelOf,
    });

    // One section row over the whole statement carries the column headings, as on the balance sheet.
    // Only over rows: an empty statement keeps the table head above its empty state.
    const section = labels.section;
    const rows: AccountsRow[] =
        section === undefined || statementRows.length === NOTHING
            ? statementRows
            : [
                  {
                      kind: 'section',
                      id: INCOME_STATEMENT_SECTION_ID,
                      label: section,
                  },
                  ...statementRows,
              ];

    return {
        columns: cells.map((cell) => cell.column),
        rows,
        filterGroups: [togglesOf(labels, view, withZeroAccounts), ...comparisonGroupOf(labels, comparisonYears)],
        showStatus: false,
    };
}

/**
 * The base of every percentage, per period: the percent-base node as a positive number.
 *
 * `null` where there is nothing to measure against — dividing by nothing would print percentages that
 * describe an empty statement.
 */
function basesOf(values: readonly (number | null)[] | undefined): (period: number) => number | null {
    return (period) => {
        const value = values?.[period] ?? null;
        return value === null || value === NOTHING ? null : Math.abs(value);
    };
}

/** One column, and how its cell is filled from a row's values per period. */
interface CellPlan {
    readonly column: AccountsColumn;
    readonly of: (values: readonly (number | null)[]) => number | null;
}

/**
 * Which columns exist, and how each is filled — ONE list, so the headings and the cells cannot drift.
 *
 * The toggles add and remove whole columns. Building the two independently is how a table ends up
 * showing a percentage under a heading that says "Δ absolut". An account, a position, a total and a
 * subtotal fill their cells the same way: only their values differ.
 */
function cellPlanOf(
    labels: IncomeStatementLabels,
    view: IncomeStatementView,
    baseOf: (period: number) => number | null,
): CellPlan[] {
    const plan: CellPlan[] = [
        {
            column: { label: labels.currentColumn, kind: 'saldo', current: true },
            of: (values) => valueAt(values, CURRENT),
        },
    ];

    if (view.percentOfRevenue) {
        plan.push({
            column: { label: labels.percentColumn, kind: 'percent' },
            of: (values) => shareOf(valueAt(values, CURRENT), baseOf(CURRENT)),
        });
    }

    labels.comparisonColumns.forEach((comparison, position) => {
        const period = position + 1;

        // The prior year is NOT optional — but neither is it special here. It is simply the first
        // comparison column, and a screen with no comparison period at all shows none rather than an
        // empty one that reads as "nothing was booked last year".
        plan.push({ column: { label: comparison.label, kind: 'saldo' }, of: (values) => valueAt(values, period) });

        if (view.percentOfRevenue) {
            // Each period against its own operating revenue.
            plan.push({
                column: { label: labels.percentColumn, kind: 'percent' },
                of: (values) => shareOf(valueAt(values, period), baseOf(period)),
            });
        }

        // What the toggle governs: the two CHANGE columns — and only against the FIRST comparison.
        // A Δ per additional year would double every column the user adds, and "Vorjahresvergleich"
        // names one comparison: the one against last year.
        if (view.priorYearComparison && position === FIRST) {
            plan.push(
                {
                    column: { label: labels.deltaAbsoluteColumn, kind: 'deltaAbsolute' },
                    of: (values) => difference(valueAt(values, CURRENT), valueAt(values, period)),
                },
                {
                    column: { label: labels.deltaPercentColumn, kind: 'deltaPercent' },
                    of: (values) => changeOf(valueAt(values, CURRENT), valueAt(values, period)),
                },
            );
        }
    });

    return plan;
}

function valueAt(values: readonly (number | null)[], period: number): number | null {
    return values[period] ?? null;
}

/** A share of the base, unsigned, or `null` without a base or a value. */
function shareOf(amount: number | null, base: number | null): number | null {
    return amount === null || base === null ? null : (Math.abs(amount) / base) * PERCENT;
}

function difference(current: number | null, prior: number | null): number | null {
    return current === null || prior === null ? null : current - prior;
}

/**
 * The change in percent, in the display sign. `null` from a period of zero: a change from nothing has
 * no percentage, and "+100%" would be a statement the numbers do not support.
 */
function changeOf(current: number | null, prior: number | null): number | null {
    return current === null || prior === null || prior === NOTHING
        ? null
        : ((current - prior) / Math.abs(prior)) * PERCENT;
}

/** The analysis toggles plus the zero-accounts switch, on the left. */
function togglesOf(labels: IncomeStatementLabels, view: IncomeStatementView, withZeroAccounts: boolean): FilterGroup {
    return {
        id: 'analysis',
        align: 'start',
        chips: [
            ...INCOME_STATEMENT_TOGGLES.map((id) => ({
                id,
                label: labels.toggle(id),
                active: view[id],
            })),
            // Beside the toggles but NOT one of them: the two above change what the loaded rows look
            // like, this one changes which rows are loaded. The container routes it elsewhere.
            zeroAccountsChipOf(labels.zeroAccountsChip, labels.zeroAccountsLockedReason ?? null, withZeroAccounts),
        ],
    };
}

/** The comparison years, on the right. Rendered when there are any; inert either way. */
function comparisonGroupOf(labels: IncomeStatementLabels, years: readonly ComparisonYear[]): FilterGroup[] {
    return years.length === NOTHING
        ? []
        : [
              {
                  id: 'comparisonYears',
                  align: 'end',
                  label: labels.comparisonYearsLabel,
                  chips: years.map((year) => ({ id: year.id, label: year.label, active: year.active })),
              },
          ];
}

/**
 * The accounts of this statement that a TRUSTEE has to decide about — the ones the classifier tried
 * and gave up on (`AI_UNRESOLVED`).
 *
 * ⚠️ It filtered on `accountGroup === null` until 24.08.2026, which lumped two opposite situations
 * together: an account the machine is still working on and an account the machine has abandoned. Both
 * carry no position, so both looked like a to-do. The consequence was visible before any polling
 * existed — the assignment dialog offered accounts the classifier was about to resolve, and they
 * vanished from under the user on the next read.
 *
 * `PENDING` is deliberately NOT here. It belongs in a progress indicator, never in a work list: this
 * set may only shrink when a human assigns something, which is what lets the dialog walk it without
 * the ground moving.
 */
export function unassignedIncomeStatementAccounts(accounts: readonly IProjectAccount[]): IProjectAccount[] {
    return accountsOfStatement(accounts).filter((account) => account.groupClassification === 'AI_UNRESOLVED');
}
